// One-click dialer. Compact = 28px icon button for table rows; full = primary
// button for headers. Dropdown picks browser SDK or phone-mode (server rings
// the agent's mobile, then bridges to the lead). Modal lets the user edit the
// number before dialing — saved lead phone never mutates.

import React, { useEffect, useRef, useState } from "react";
import { PhoneCall, Globe, Smartphone, X, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { Button, IconButton, Input, Select, Label } from "../../atoms";
import { useInitiateCall } from "../../hooks/useLeads";
import { useActiveCall } from "../../contexts/ActiveCallProvider";

function formatErr(err) {
  return err?.response?.data?.error || err?.message || "Failed to start call";
}

export default function CallButton({ leadId, leadPhone, leadTitle, compact = false, agentPhone: agentPhoneProp }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [modalMode, setModalMode] = useState(null);
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("IN");
  const [agentPhone, setAgentPhone] = useState(agentPhoneProp || "");
  const ref = useRef(null);

  const initiate = useInitiateCall(leadId);
  const { activeCall, startCall } = useActiveCall();

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  const openModal = (mode) => {
    setMenuOpen(false);
    setModalMode(mode);
    setPhone(leadPhone || "");
    setAgentPhone(agentPhoneProp || "");
  };
  const closeModal = () => {
    setModalMode(null);
    setPhone("");
  };

  const submit = async () => {
    if (!phone.trim()) { toast.error("Enter a phone number"); return; }
    if (modalMode === "browser" && activeCall) {
      toast.error("A call is already in progress. End it before starting another.");
      return;
    }
    if (modalMode === "phone" && !agentPhone.trim()) {
      toast.error("Enter your phone number to receive the call");
      return;
    }
    try {
      const payload = { to: phone.trim(), mode: modalMode, defaultCountry: country };
      if (modalMode === "phone") payload.agentPhone = agentPhone.trim();
      const result = await initiate.mutateAsync(payload);
      if (modalMode === "phone") {
        toast.success("Call started — your phone will ring shortly");
        closeModal();
      } else {
        startCall({
          leadId,
          callLogId: result.callLogId,
          to: result.dialTarget || result.to,
          leadTitle,
          provider: result.provider || "plivo",
        });
        closeModal();
      }
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  return (
    <>
      <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
        {compact ? (
          <IconButton
            size="sm"
            aria-label="Call lead"
            title="Call lead"
            onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
            style={{ color: "var(--crm-primary)" }}
          >
            <PhoneCall size={14} />
          </IconButton>
        ) : (
          <Button
            variant="primary"
            size="md"
            leftIcon={<PhoneCall size={15} />}
            onClick={() => setMenuOpen((v) => !v)}
          >
            Call
          </Button>
        )}

        {menuOpen && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 60,
              width: 240, background: "var(--color-bg-surface)",
              border: "1px solid var(--color-border-subtle)",
              borderRadius: 8, overflow: "hidden",
              boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
            }}
          >
            <MenuItem
              icon={<Globe size={16} style={{ color: "var(--crm-primary)" }} />}
              title="Call via Browser"
              hint="Use your computer mic"
              onClick={() => openModal("browser")}
            />
            <div style={{ height: 1, background: "var(--color-border-subtle)" }} />
            <MenuItem
              icon={<Smartphone size={16} style={{ color: "var(--crm-success)" }} />}
              title="Call via My Phone"
              hint="Plivo rings your mobile first"
              onClick={() => openModal("phone")}
            />
          </div>
        )}
      </div>

      {modalMode && (
        <div
          onClick={closeModal}
          style={{
            position: "fixed", inset: 0, zIndex: 70,
            background: "rgba(0,0,0,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%", maxWidth: 440,
              background: "var(--color-bg-base)",
              border: "1px solid var(--color-border-subtle)",
              borderRadius: 8, overflow: "hidden",
              boxShadow: "0 24px 48px rgba(0,0,0,0.35)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid var(--color-border-subtle)" }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: "var(--color-text-primary)" }}>
                {modalMode === "browser" ? "Call via Browser" : "Call via My Phone"}
              </h3>
              <IconButton size="sm" aria-label="Close" onClick={closeModal}><X size={16} /></IconButton>
            </div>

            <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <Label>Lead phone number</Label>
                <Input
                  autoFocus
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+91 98765 43210"
                />
                <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
                  Edit before dialing — the saved lead phone won't change.
                </span>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <Label>Default country (if no + prefix)</Label>
                <Select value={country} onChange={(e) => setCountry(e.target.value)}>
                  <option value="IN">India (+91)</option>
                  <option value="US">United States (+1)</option>
                  <option value="GB">United Kingdom (+44)</option>
                  <option value="AU">Australia (+61)</option>
                </Select>
              </div>

              {modalMode === "phone" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <Label>Your phone number</Label>
                  <Input
                    type="tel"
                    value={agentPhone}
                    onChange={(e) => setAgentPhone(e.target.value)}
                    placeholder="+91 98765 43210"
                  />
                  <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
                    Plivo dials this first, then bridges to the lead.
                  </span>
                </div>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "12px 18px", borderTop: "1px solid var(--color-border-subtle)", background: "var(--color-bg-surface)" }}>
              <Button variant="ghost" size="sm" onClick={closeModal}>Cancel</Button>
              <Button
                variant="primary"
                size="sm"
                onClick={submit}
                disabled={initiate.isPending}
                leftIcon={initiate.isPending ? <Loader2 size={13} className="animate-spin" /> : <PhoneCall size={13} />}
              >
                Call
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function MenuItem({ icon, title, hint, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 12,
        padding: "12px 14px", background: "transparent", border: "none",
        textAlign: "left", cursor: "pointer", color: "var(--color-text-primary)",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "var(--color-bg-muted)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
    >
      {icon}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{title}</div>
        <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{hint}</div>
      </div>
    </button>
  );
}
